"use client";

// WO-GF-001 B4.0 — the ported map system. 1:1 capability port of the live map widget
// (`snowbus-app/src/widgets/Map`), machinery replaced: the abandoned `google-map-react` becomes
// `@vis.gl/react-google-maps` (Google's officially-maintained React wrapper), rendering the IDENTICAL
// Google Map — same style modes (map-options.ts, copied verbatim from legacy option.js), same custom
// SVG markers, same hover name-popover, same default center/zoom. R6-6 fix: this is real machinery, not
// a photocopy — with a key set it renders live tiles; the marker/popover interactions are our own code
// and run with or without a key.
//
// Key handling (WO-GF-001 B4): NEXT_PUBLIC_GOOGLE_MAPS_KEY is a founder-rail client key, not committed.
// Without it the map shows a faithful placeholder (region masked in the gate, date-picker precedent) and
// the marker layer still renders so the interaction machinery stays exercisable.

import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
// S33 · F192 — one pin module, shared with KeylessMap.
import { pinMarkerSvg, type PinKind } from "./pin-markers";
import {
  DEFAULT_CENTER,
  DEFAULT_MAP_ID,
  DEFAULT_ZOOM,
  MAP_LIBRARIES,
  MOUNTAIN_MAP_STYLES,
  MOUNTAIN_ZOOM,
  liveMapEnabled,
  mapApiKey,
} from "@/lib/map-options";

export type PortMapMarker = {
  lat: number;
  lng: number;
  name?: string;
  /** legacy flag: "plane" = airport pin, else the blue pin. Mountain mode uses the mountain pin. */
  flag?: "plane" | "other";
  /** optional supplier/operator logo shown as the pin instead of the default svg. */
  logo?: string;
  key?: string;
};

export type PortMapProps = {
  markers: PortMapMarker[];
  /** "default" = legacy isMountain=false (mapId cloud style); "mountain" = legacy isMountain=true (JSON style). */
  mode?: "default" | "mountain";
  center?: { lat: number; lng: number };
  zoom?: number;
  className?: string;
  /** legacy rightTopIcon — operator/supplier logo overlaid top-right of the map. */
  rightTopIcon?: ReactNode;
  /** test/label hook — surfaces which surface mounted the map (e.g. "profile-getting-there"). */
  surface?: string;
};

// S33 · F192 — the three flat mirrored images are GONE from this file. It was the second marker
// implementation in the codebase (KeylessMap was the first), so "the markers are wrong" had to be fixed in
// two places or not at all. Both now render from `pin-markers.ts`, which is the single SVG source the WO
// requires and the first citizen of the asset-registry direction.
//
// A SUPPLIER/OPERATOR LOGO STILL WINS when the marker carries one — that is deliberate and unchanged.
// The founder's finding is about the DEFAULT pin, and a real supplier logo on the map is content, not a
// placeholder glyph. Only the fallback moved to the shared module.
function markerPinKind(m: PortMapMarker, mode: "default" | "mountain"): PinKind {
  if (m.flag === "plane") return "airport";
  return mode === "mountain" ? "mountain" : "stop";
}

// A single marker: custom SVG pin + name label, name popover on hover (legacy OverlayTrigger/Popover).
function MarkerContent({ marker, mode }: { marker: PortMapMarker; mode: "default" | "mountain" }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="portMapMarker"
      data-map-marker=""
      data-name={marker.name || ""}
      data-lat={marker.lat}
      data-lng={marker.lng}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: "relative", display: "inline-flex", flexDirection: "column", alignItems: "center" }}
    >
      {marker.logo ? (
        // a REAL supplier/operator logo is content and still wins over any glyph
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={marker.logo}
          alt={marker.name || "map marker"}
          // Inline dimensions so a captured page's global `img { height:auto }` rule can't scale the pin.
          style={{ height: mode === "mountain" ? 28 : 22, width: "auto", maxWidth: "none", display: "block" }}
        />
      ) : (
        // S33 · F192 — the pin from the ONE module. `dangerouslySetInnerHTML` carries no user input: the
        // markup is built entirely from constants in `pin-markers.ts` (live's own captured vector paths)
        // plus a category enum, so there is no interpolation path a value could ride in on.
        <span
          data-marker-kind={markerPinKind(marker, mode)}
          style={{ display: "block", lineHeight: 0 }}
          dangerouslySetInnerHTML={{
            __html: pinMarkerSvg(markerPinKind(marker, mode), mode === "mountain" ? 28 : 22),
          }}
        />
      )}
      {marker.name ? (
        <span className="portMapMarkerLabel fw-bold" style={{ fontSize: 12, whiteSpace: "nowrap", marginTop: 2 }}>
          {marker.name}
        </span>
      ) : null}
      {marker.name && hover ? (
        <div
          role="tooltip"
          className="portMapPopover"
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#fff",
            border: "1px solid #e3e6ef",
            borderRadius: 6,
            padding: "4px 8px",
            fontSize: 12,
            whiteSpace: "nowrap",
            boxShadow: "0 2px 8px rgba(22,46,77,0.18)",
            zIndex: 10,
          }}
        >
          {marker.name}
        </div>
      ) : null}
    </div>
  );
}

// The interactive marker OVERLAY: renders every marker as a DOM pin (custom SVG + name + hover popover).
// This is the geo-independent "port the capability" layer — it works with or without tiles, so the
// marker/popover machinery is always present and exercisable (behavior gate). Shown until real tiles
// paint (then AdvancedMarkers take over) and as the standing state when no key / a referrer-blocked key.
function MarkerOverlay({ markers, mode }: { markers: PortMapMarker[]; mode: "default" | "mountain" }) {
  return (
    <div
      className="portMapMarkerLayer"
      style={{
        // On top of (and hiding) the underlying map until it actually paints tiles — so a referrer-blocked
        // or still-loading map never shows a broken Google error state; markers stay visible + hoverable.
        // Once tiles paint (allow-listed host + key), this overlay is unmounted and the real map is revealed.
        position: "absolute",
        inset: 0,
        zIndex: 6,
        display: "flex",
        flexWrap: "wrap",
        gap: 18,
        justifyContent: "center",
        alignItems: "center",
        alignContent: "center",
        padding: 20,
        overflow: "hidden",
        background: "linear-gradient(180deg,#eef3fb 0%,#e5e8ee 100%)",
      }}
    >
      {markers.map((m, i) => (
        <MarkerContent key={m.key || `${m.name}-${m.lat}-${m.lng}-${i}`} marker={m} mode={mode} />
      ))}
    </div>
  );
}

// The real Google map (tiles) + geo-positioned AdvancedMarkers, with graceful fallback: until the map
// actually paints tiles (`onTilesLoaded`), the DOM MarkerOverlay stays up. On a host the key does not
// allow (referrer-restricted legacy key on localhost) tiles never paint, so the overlay is the standing,
// interactive representation — the port never degrades to a dead/blank region.
function RealMap({
  markers,
  mode,
  center,
  zoom,
  onFail,
}: {
  markers: PortMapMarker[];
  mode: "default" | "mountain";
  center: { lat: number; lng: number };
  zoom: number;
  onFail: () => void;
}) {
  const [painted, setPainted] = useState(false);
  // If tiles have not painted within the window, the key/mapId/billing is not valid for THIS host
  // (referrer, unconfigured mapId, billing off). Fall back to the interactive overlay and unmount the
  // map so it stops issuing failing tile requests. Uniform for every non-auth failure gm_authFailure misses.
  useEffect(() => {
    if (painted) return;
    const t = setTimeout(() => onFail(), 3500);
    return () => clearTimeout(t);
  }, [painted, onFail]);
  return (
    <>
      {/* Until tiles actually paint, the map layer is non-interactive so it can never intercept pointer
          events from the marker overlay on top (a referrer-blocked map still mounts an event layer). */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: painted ? "auto" : "none" }}>
        <Map
          style={{ width: "100%", height: "100%" }}
          defaultCenter={center}
          defaultZoom={zoom}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapTypeControl
          zoomControl
          onTilesLoaded={() => setPainted(true)}
          // DEFAULT mode = cloud-styled vector map via mapId (enables AdvancedMarker).
          // MOUNTAIN mode = JSON-styled raster map (legacy MapOption.styles); no mapId.
          {...(mode === "mountain" ? { styles: MOUNTAIN_MAP_STYLES } : { mapId: DEFAULT_MAP_ID })}
        >
          {painted && mode === "default"
            ? markers.map((m, i) => (
                <AdvancedMarker
                  key={m.key || `${m.name}-${m.lat}-${m.lng}-${i}`}
                  position={{ lat: m.lat, lng: m.lng }}
                >
                  <MarkerContent marker={m} mode={mode} />
                </AdvancedMarker>
              ))
            : null}
        </Map>
      </div>
      {!painted ? <MarkerOverlay markers={markers} mode={mode} /> : null}
    </>
  );
}

export default function PortMap({
  markers,
  mode = "default",
  center,
  zoom,
  className,
  rightTopIcon,
  surface,
}: PortMapProps) {
  const key = mapApiKey();
  const live = liveMapEnabled();
  const first = markers[0];
  const resolvedCenter = center || (first ? { lat: first.lat, lng: first.lng } : DEFAULT_CENTER);
  const resolvedZoom = zoom ?? (mode === "mountain" ? MOUNTAIN_ZOOM : DEFAULT_ZOOM);

  // Google Maps calls window.gm_authFailure() on an invalid/referrer-blocked key. When that happens the
  // real map can never paint on THIS host (e.g. the legacy key is allow-listed to snowbus.com, not
  // localhost), so we unmount it and fall back to the clean interactive overlay — no error state, no
  // endless failed tile requests. On an allow-listed host gm_authFailure never fires and tiles paint.
  const [authFailed, setAuthFailed] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  useEffect(() => {
    if (!key || !live) return;
    const prev = (window as unknown as { gm_authFailure?: () => void }).gm_authFailure;
    (window as unknown as { gm_authFailure?: () => void }).gm_authFailure = () => setAuthFailed(true);
    return () => {
      (window as unknown as { gm_authFailure?: () => void }).gm_authFailure = prev;
    };
  }, [key, live]);

  const useLiveMap = !!key && live && !authFailed && !mapFailed;
  const handleMapFail = useCallback(() => setMapFailed(true), []);

  return (
    <div
      className={`${className || ""} position-relative`}
      data-port-map=""
      data-map-mode={mode}
      data-map-key={key ? "present" : "absent"}
      data-map-live={useLiveMap ? "true" : "false"}
      data-marker-count={markers.length}
      data-map-surface={surface || ""}
      style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}
    >
      {rightTopIcon ? (
        <div className="portMapRightIcon" style={{ position: "absolute", top: 12, right: 12, zIndex: 5 }}>
          {rightTopIcon}
        </div>
      ) : null}

      {useLiveMap ? (
        <APIProvider apiKey={key} libraries={[...MAP_LIBRARIES]} version="weekly">
          <RealMap
            markers={markers}
            mode={mode}
            center={resolvedCenter}
            zoom={resolvedZoom}
            onFail={handleMapFail}
          />
        </APIProvider>
      ) : (
        <div
          className="portMapPlaceholder"
          data-port-map-placeholder=""
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            background: "linear-gradient(180deg,#eef3fb 0%,#e5e8ee 100%)",
          }}
        >
          <MarkerOverlay markers={markers} mode={mode} />
        </div>
      )}
    </div>
  );
}
